function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import cloneDeep from 'lodash/cloneDeep';
import isNil from 'lodash/isNil';
import React, { useId } from 'react';
import produce from 'immer';
import { DragDropProvider, useDraggable, useDroppable } from '@dnd-kit/react';
import { directionBiased } from '@dnd-kit/collision';
import { arrayMove } from '@dnd-kit/helpers';
import { useStagedState } from 'common/hooks/useStagedState';
import { MetricEntry, MetricEntryBehavior } from 'common/query-builder/query-entry-section/types';
import type { RowProps } from '../types/catalog';
import styles from './event-row/index.module.styl';
import { BehaviorEventRow, EventRowProps } from './event-row';

/**
 * Renders a BehaviorEventRow with drop targets above and below it for
 * changing the order of behavior steps
 */
export function DraggableBehavior(props: EventRowProps & {
  parentIndex?: number;
  isLast?: boolean;
}) {
  var _props$parentIndex, _props$parentIndex2;
  const id = useId();
  const dragId = "".concat(id, "-").concat((_props$parentIndex = props.parentIndex) !== null && _props$parentIndex !== void 0 ? _props$parentIndex : "", "-").concat(props.index);
  const index = (_props$parentIndex2 = props.parentIndex) !== null && _props$parentIndex2 !== void 0 ? _props$parentIndex2 : props.index;
  const {
    ref,
    handleRef
  } = useDraggable({
    id: "".concat(dragId, "-draggable"),
    feedback: "clone",
    data: {
      index
    }
  });
  const {
    ref: aboveDropRef,
    isDropTarget: isDroppingAbove
  } = useDroppable({
    id: "".concat(dragId, "-above"),
    collisionDetector: directionBiased,
    data: {
      index: Math.max(index - 1, 0)
    }
  });
  const {
    ref: belowDropRef,
    isDropTarget: isDroppingBelow
  } = useDroppable({
    id: "".concat(dragId, "-below"),
    collisionDetector: directionBiased,
    data: {
      index: index + 1
    }
  });
  const isNested = !isNil(props.parentIndex);
  const disableDrag = isNested ? props.index !== 0 : false;
  const disableDropBelow = isNested ? !props.isLast : false;
  const disableDropAbove = isNested ? props.index !== 0 : false;
  return <>
      {!disableDropAbove && <div className={styles.dropTarget} ref={aboveDropRef}>
          {isDroppingAbove && <div className={styles.indicator} />}
        </div>}

      <BehaviorEventRow {...props} ref={ref} handleRef={!disableDrag ? handleRef : undefined} data-sentry-element="BehaviorEventRow" data-sentry-source-file="behaviors-list.tsx" />

      {!disableDropBelow && <div className={styles.dropTarget} ref={belowDropRef}>
          {isDroppingBelow && <div className={styles.indicator} />}
        </div>}
    </>;
}
export function DraggableBehaviorsList(props: RowProps & {
  children: Array<React.ReactElement<typeof DraggableBehavior>>;
  setBehaviors: (behaviors: MetricEntryBehavior[], commit?: boolean) => void;
}) {
  return <DragDropProvider onDragEnd={event => {
    const {
      source,
      target
    } = event.operation;
    if (event.operation.canceled || !props.entry.behavior.behaviors || !source || !target) {
      return;
    }
    const from = source.data.index;
    const to = Math.min(props.entry.behavior.behaviors.length - 1, target.data.index);
    if (from === to) {
      return;
    }
    const updatedBehaviors = arrayMove(props.entry.behavior.behaviors, from, to);
    props.setBehaviors(updatedBehaviors, true);
  }} data-sentry-element="DragDropProvider" data-sentry-component="DraggableBehaviorsList" data-sentry-source-file="behaviors-list.tsx">
      <div>{props.children}</div>
    </DragDropProvider>;
}

/**
 * Hook to manage a list of behaviors for a MetricEntry.
 * @todo(metrics) tracking
 */
export function useBehaviorsList(entry: MetricEntry, onUpdate: RowProps[`updateEntry`]) {
  var _entry$behavior$behav;
  const [behaviors, setBehaviors] = useStagedState((_entry$behavior$behav = entry.behavior.behaviors) !== null && _entry$behavior$behav !== void 0 ? _entry$behavior$behav : [], state => {
    onUpdate(produce(entry, draft => {
      draft.behavior.behaviors = state;
    }));
  });
  const handleAddBehavior = (newBehavior: MetricEntryBehavior) => {
    setBehaviors([...behaviors, newBehavior], true);
  };
  const handleUpdateBehavior = (index: number, updatedBehavior: MetricEntryBehavior) => {
    setBehaviors(produce(behaviors, draft => {
      draft[index] = updatedBehavior;
    }), true);
  };
  const handleDeleteBehavior = (index: number) => {
    setBehaviors(produce(behaviors, draft => {
      draft.splice(index, 1);
    }), true);
  };
  const handleDuplicateBehavior = (index: number) => {
    setBehaviors(produce(behaviors, draft => {
      const behavior = _objectSpread(_objectSpread({}, cloneDeep(draft[index])), {}, {
        renamed: undefined
      });
      draft.splice(index + 1, 0, behavior);
    }), true);
  };
  return {
    behaviors,
    setBehaviors,
    handleAddBehavior,
    handleUpdateBehavior,
    handleDeleteBehavior,
    handleDuplicateBehavior
  };
}